import type {FC} from 'react';
import {useMemo} from 'react';
import {styled} from 'styled-components';

import type {NzbMediaMetadata, NzbMediaTrack} from '@repo/shared/api/nzb_media_metadata';

import {Tooltip} from '@repo/shared-web/components/core/tooltip';

interface NzbTrackLanguagesProps {
  metadata: NzbMediaMetadata | undefined;
  kind: 'audioTracks' | 'subtitleTracks';
}

function shortLanguage(track: NzbMediaTrack): string {
  for (const language of [track.languageBcp47, track.language, track.languageLegacy]) {
    if (!language) continue;
    try {
      // Canonicalization also converts ISO 639-2 aliases, e.g. eng -> en and fre/fra -> fr.
      const code = Intl.getCanonicalLocales(language.trim().replaceAll('_', '-'))[0]?.split('-')[0];
      if (code && /^[a-z]{2,3}$/u.test(code)) return code;
    } catch {
      // An invalid extended tag can still have a usable legacy language field.
    }
  }
  return 'und';
}

export const NzbTrackLanguages: FC<NzbTrackLanguagesProps> = ({metadata, kind}) => {
  const languages = useMemo(() => {
    if (metadata?.status !== 'complete') return undefined;
    const priority = (code: string) => (code === 'en' ? 0 : code === 'fr' ? 1 : 2);
    return [...new Set(metadata[kind].map(shortLanguage))].sort(
      (left, right) => priority(left) - priority(right) || left.localeCompare(right, 'en'),
    );
  }, [metadata, kind]);
  const label = kind === 'audioTracks' ? 'audio' : 'subtitle';

  if (metadata?.status === 'suspicious') {
    if (metadata.errorCode) {
      return (
        <Wrapper>
          <Missing title={metadata.reason}>—</Missing>
        </Wrapper>
      );
    }
    return (
      <Wrapper>
        <NoTracks $suspicious title={metadata.reason}>
          None
        </NoTracks>
      </Wrapper>
    );
  }
  if (metadata?.status === 'error') {
    return (
      <Wrapper>
        <TrackError title={[metadata.errorCode, metadata.reason].filter(Boolean).join(': ')}>
          Error
        </TrackError>
      </Wrapper>
    );
  }
  if (languages?.length === 0) {
    return (
      <Wrapper>
        <NoTracks title={`No embedded ${label} tracks`}>None</NoTracks>
      </Wrapper>
    );
  }
  if (!languages) {
    return (
      <Wrapper>
        <Missing title="Track metadata unavailable">—</Missing>
      </Wrapper>
    );
  }

  const preferredLanguages = languages.filter(language => language === 'en' || language === 'fr');
  const otherLanguages = languages.filter(language => language !== 'en' && language !== 'fr');

  return (
    <Wrapper>
      <PreferredLanguages>
        {preferredLanguages.map(
          (language, index) => `${index === 0 ? '' : ', '}${language.toUpperCase()}`,
        )}
      </PreferredLanguages>
      {otherLanguages.length > 0 ? (
        <Tooltip content={`Other ${label} languages: ${otherLanguages.join(', ').toUpperCase()}`}>
          <MoreLanguages
            aria-label={`Other ${label} languages: ${otherLanguages.join(', ')}`}
            tabIndex={0}
          >
            +{otherLanguages.length}
          </MoreLanguages>
        </Tooltip>
      ) : null}
    </Wrapper>
  );
};
NzbTrackLanguages.displayName = 'NzbTrackLanguages';

const Wrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
  white-space: nowrap;
`;

const PreferredLanguages = styled.span`
  text-transform: uppercase;
`;

const NoTracks = styled.span<{$suspicious?: boolean}>`
  color: ${p => (p.$suspicious ? '#ffcb8a' : '#ffffffbb')};
`;

const TrackError = styled.span`
  color: #ff8585;
`;

const Missing = styled.span`
  color: #8b9099;
`;

const MoreLanguages = styled.span`
  padding: 1px 5px;
  border-radius: 4px;
  background: #ffffff12;
  color: #ffffffaa;
  font-size: 12px;
  line-height: 18px;
  cursor: help;
  &:hover,
  &:focus-visible {
    background: #ffffff24;
    color: #ffffff;
  }
`;
